import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/auth';
import { TEXTURES } from '@/lib/assets';
import type { ConnectionStatus } from '@/types/game';

export default function LandingPage(): JSX.Element {
  const [status, setStatus] = useState<ConnectionStatus>('checking');
  const [detail, setDetail] = useState<string>('');
  const authStatus = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const isSignedIn = authStatus === 'authenticated' && user !== null;

  useEffect(() => {
    let cancelled = false;

    async function ping(): Promise<void> {
      try {
        const { error } = await supabase.auth.getSession();
        if (cancelled) return;
        if (error) {
          setStatus('error');
          setDetail(error.message);
          return;
        }
        setStatus('connected');
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setDetail(err instanceof Error ? err.message : 'Unknown error');
      }
    }

    void ping();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="relative min-h-dvh flex items-center justify-center px-4 sm:px-6 py-12 overflow-hidden">
      {/* Full-screen scenic background */}
      <img
        src="/art/backgrounds/dock_at_night.webp"
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover"
        fetchPriority="high"
      />
      {/* Graduated dark overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/40" />
      <div className="absolute inset-0 bg-gradient-to-b from-sea-deep/30 via-transparent to-transparent" />

      <div className="relative w-full max-w-xl stage-fade-in">
        {/* Central parchment card */}
        <div
          className="relative overflow-hidden rounded-lg border-2 border-wood-mid/50 shadow-[0_8px_40px_rgba(0,0,0,0.7)]"
          style={{
            backgroundImage: `url("${TEXTURES.parchment}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-parchment/80 pointer-events-none" />

          <div className="relative px-6 sm:px-10 py-10 sm:py-14 text-center">
            <p className="text-candle-dark text-[0.65rem] sm:text-xs uppercase tracking-[0.5em] mb-4 sm:mb-6">
              1670 &middot; The Caribbean
            </p>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-wood-dark mb-3 sm:mb-4 leading-[1.1]">
              Saltbound
            </h1>

            {/* Decorative divider */}
            <div className="flex items-center justify-center gap-3 mb-6 sm:mb-8 text-candle/50">
              <span aria-hidden className="h-px w-12 sm:w-16 bg-candle/40" />
              <span aria-hidden className="text-candle-dark">
                &#9670;
              </span>
              <span aria-hidden className="h-px w-12 sm:w-16 bg-candle/40" />
            </div>

            <p className="text-ink-mid text-base sm:text-lg leading-relaxed text-balance mb-8 sm:mb-10 italic">
              A pirate&rsquo;s tale in the wickedest city on earth &mdash; where the Crown is for
              sale, the cutlass settles every account, and the only honest man is the one with gold
              in his hand.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 mb-8 sm:mb-10">
              {isSignedIn ? (
                <Link to="/game" className="landing-btn-primary">
                  Continue Your Tale
                </Link>
              ) : (
                <>
                  <Link to="/signup" className="landing-btn-primary">
                    Enlist
                  </Link>
                  <Link to="/login" className="landing-btn-secondary">
                    Sign In
                  </Link>
                </>
              )}
            </div>

            <ConnectionBadge status={status} detail={detail} />
          </div>
        </div>
      </div>
    </main>
  );
}

function ConnectionBadge({
  status,
  detail,
}: {
  status: ConnectionStatus;
  detail: string;
}): JSX.Element {
  const label =
    status === 'checking'
      ? 'Hailing the harbormaster…'
      : status === 'connected'
        ? 'Harbormaster has the ledgers open'
        : status === 'error'
          ? 'Harbormaster has not answered'
          : 'Idle';

  const dotClass =
    status === 'connected'
      ? 'bg-emerald-500'
      : status === 'error'
        ? 'bg-blood-bright'
        : 'bg-candle animate-pulse';

  return (
    <div className="inline-flex flex-col items-center gap-2">
      <div className="inline-flex items-center gap-2.5 rounded-full border border-wood-mid/30 bg-parchment-light/60 px-4 py-2 text-xs text-ink-mid shadow-sm">
        <span className={`inline-block h-2 w-2 rounded-full ${dotClass}`} aria-hidden />
        <span>{label}</span>
      </div>
      {status === 'error' && detail && (
        <p className="text-xs text-blood-bright/90 max-w-sm">{detail}</p>
      )}
    </div>
  );
}
